import { InputHTMLAttributes, useEffect, useState } from 'react';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { availabilityTypeConstant, bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { SearchBar } from 'us-react-components/dist/SearchBar/SearchBar';
import { Facility, FavoriteSitterService, ParentSearchBar, Sitter, SitterService, UserGeocode } from 'us-web-services';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';
import { Routes } from '../../Routes';
import { BestMatch } from '../../components/Parent/Dashboard/components/bestMatch/BestMatch';
import { DayCaresBestMatch } from '../../components/Parent/Dashboard/components/daycaresBestMatch/DayCaresBestMatch';
import { Favorites } from '../../components/Parent/Dashboard/components/favorites/Favorites';
import { CompanyGroups } from '../../components/Parent/Dashboard/components/groups/CompanyGroups';
import { MyGroups } from '../../components/Parent/Dashboard/components/groups/MyGroups';
import { AgencyCareWidget } from '../../components/Parent/Dashboard/components/widgets/AgencyCareWidget';
import { FacebookWidget } from '../../components/Parent/Dashboard/components/widgets/FacebookWidget';
import { getScheduleTypeFromAvailability } from '../../utils';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  bestMatchFacilities: Facility[];
  bestMatchSitters: Sitter[];
  geocode: UserGeocode;
  getWillSitParams(search: ParentSearchBar): object;
  handlePostAJob(): void;
  isAgencyCareAvailable: boolean;
  loadingBestMatch: boolean;
  search: ParentSearchBar;
  searchChanged(newSearch: ParentSearchBar): void;
}

const MainContainer = (props: Props): JSX.Element => {
  const { bestMatchFacilities, bestMatchSitters, geocode, handlePostAJob, getWillSitParams, isAgencyCareAvailable, loadingBestMatch, search, searchChanged } = props;

  const cookies = new Cookies();
  const navigate = useNavigate();
  const { services: { tracker }, stores } = useDependencies();
  const modal = useModal();

  const { parent } = stores.parent;
  const { user } = stores.user;
  const { companies } = stores.companyEmployees;

  const [loadingFavoriteSitters, setLoadingFavoriteSitters] = useState(true);
  const [favoriteSitters, setFavoriteSitters] = useState<Sitter[]>([]);
  const [favoritesCount, setFavoritesCount] = useState<number>();

  const handleNavigateToSearch = (bookingVerticalTypeId: number | undefined): void => {
    const originalParams = {
      verticalTypeId: bookingVerticalTypeId?.toString() ?? (search.vertical?.id ?? bookingVerticalType.CHILD_CARE).toString(),
      zip: search.zip ?? '',
      city: geocode?.city ?? '',
      state: geocode?.state ?? '',
      distance: search.distance ? search.distance.toString() : '',
      availabilityType: availabilityTypeConstant.find((a: { id?: number; }) => a.id === search.searchType?.id)?.type ?? 'All',
      searchTypeCode: availabilityTypeConstant.find((a: { id?: number; }) => a.id === search.searchType?.id)?.code ?? 'ALL',
      scheduleTypes: getScheduleTypeFromAvailability(search.searchType?.id)?.toString() ?? '',
      latitude: geocode?.latitude?.toString() ?? '',
      longitude: geocode?.longitude?.toString() ?? '',
    };

    const additionalParams = getWillSitParams(search);
    const params = {
      ...originalParams,
      ...additionalParams,
    };

    navigate({
      pathname: generatePath(Routes.parent.sitters),
      search: `${createSearchParams(params)}`,
    });
  }

  const openAddPetCareModal = (): void => {
    modal?.setModalName(ModalName.AddPetCare);
  };

  const openBookingRecurringSuccessModal = (): void => {
    modal?.setModalName(ModalName.BookingRecurringSuccess);
  };

  const openBookingAgencySuccessModal = (): void => {
    modal?.setModalName(ModalName.BookingAgencySuccess);
  };

  const handleClickViewResults = (bookingVerticalTypeId: number | undefined): void => {
    if (search.vertical?.id === bookingVerticalType.PET_CARE && (!parent.dog && !parent.cat && !parent.otherPet)) {
      stores.parent.checkParentUpdated(() => handleNavigateToSearch(bookingVerticalTypeId), openAddPetCareModal);
    } else {
      handleNavigateToSearch(bookingVerticalTypeId);
    }
  }

  const getFavorites  = async (): Promise<void> => {
    const config = {
      params: {
        userId: user.id,
        parentId: user.id,
        page: 1,
        bookmarked: true,
      },
    };
    const response = (await SitterService.getByFilter(config)).data;

    setFavoriteSitters(response.data);
    setFavoritesCount(response.meta.size);
    setLoadingFavoriteSitters(false);
  }

  const setFavorite = async (isFavorited: boolean, favoriteId: number, sitterId: number): Promise<void> => {
    const config = {
      parentId: user.id,
      sitterId,
    };

    if (isFavorited) {
      await FavoriteSitterService.create(config);
    } else {
      await FavoriteSitterService.delete(favoriteId);
    }
  }

  const trackEvent = async (event: string, params?: {elementText: string}): Promise<void> => tracker.track(event, params);

  useEffect(() => {
    getFavorites();

    const bookingAgencySuccess = cookies.get(Storage.AGENCY_BOOKING_SUCCESS);
    const bookingRecurringSuccess = cookies.get(Storage.RECURRING_SUCCESS);

    if (bookingRecurringSuccess) {
      openBookingRecurringSuccessModal();
    } else if (bookingAgencySuccess) {
      openBookingAgencySuccessModal();
    }
  }, []);

  return (
    <div className='w-full md:2/3 lg:w-3/4 md:ml-16'>
      <div className='mb-8 px-0'>
        {search.zip && (<SearchBar changeCallback={searchChanged} trackingCallback={trackEvent} search={search} /> )}
      </div>
      {companies.length > 0 && (
        <CompanyGroups companies={companies} newSearch={search} geocode={geocode} getWillSitParams={getWillSitParams} />
      )}
      <div className='w-full mb-5 md:mb-10 border-b-2 border-neutral-100'>
        {search.vertical?.id !== bookingVerticalType.DAY_CARE && (
          <BestMatch
            sitters={bestMatchSitters}
            hideExperience={!!search.vertical?.id && search.vertical?.id !== bookingVerticalType.CHILD_CARE
              && search.vertical?.id !== bookingVerticalType.SITTERS_NANNIES_BACKUP_CARE}
            setFavorite={setFavorite}
            handleClickViewResults={handleClickViewResults}
            handlePostAJob={handlePostAJob}
            loading={loadingBestMatch}
          />
        )}
        {search.vertical?.id === bookingVerticalType.CHILD_CARE && bestMatchFacilities.length > 0 && (
          <DayCaresBestMatch
            facilities={bestMatchFacilities}
            handleClickViewResults={handleClickViewResults}
            handlePostAJob={handlePostAJob}
            loading={loadingBestMatch}
          />
        )}
        {search.vertical?.id === bookingVerticalType.DAY_CARE && (
          <DayCaresBestMatch
            facilities={bestMatchFacilities}
            handleClickViewResults={handleClickViewResults}
            handlePostAJob={handlePostAJob}
            hideTitle
            loading={loadingBestMatch}
          />
        )}
      </div>
      {(search.vertical?.id !== bookingVerticalType.DAY_CARE) && (
        <div className='w-full mb-5 md:mb-10'>
          {favoritesCount !== 0 && (
            <Favorites
              bestMatchSitters={bestMatchSitters}
              city={geocode.city}
              count={favoritesCount}
              loading={(loadingBestMatch || loadingFavoriteSitters)}
              pictureUrl={parent.pictureUrl}
              sitters={favoriteSitters}
              state={geocode.state}
              zip={geocode.zip}
              navigateToSearchPage={handleClickViewResults}
              setFavorite={setFavorite}
            />
          )}
          <div className='w-full mb-5 md:mb-10'>
            <MyGroups newSearch={search} geocode={geocode} getWillSitParams={getWillSitParams} />
          </div>
          {isAgencyCareAvailable && (
            <div className='my-5 flex md:hidden'>
              <AgencyCareWidget city={geocode.city} state={geocode.state} zip={geocode.zip} />
            </div>
          )}
          {!user?.facebookId && (
            <div className='mb-5 xs:flex sm:flex md:hidden'>
              <FacebookWidget />
            </div>
          )}
          {favoritesCount === 0 && (
            <Favorites
              bestMatchSitters={[]}
              city={geocode.city}
              count={favoritesCount}
              loading={false}
              pictureUrl={parent.pictureUrl}
              sitters={[]}
              state={geocode.state}
              zip={geocode.zip}
              navigateToSearchPage={handleClickViewResults}
              setFavorite={async (): Promise<void> => { Promise.resolve() }}
            />
          )}
        </div>
        )
      }
    </div>
  );
};

export { MainContainer };
